import { useRef } from 'react';
import type { JSX } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { FormattedMessage } from 'react-intl';
import { FieldArray, Field } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import Heading from '../../../common/Heading';
import formStyles from '../../../styles/forms.module.scss';
import s from './StepWindFacilityData.module.scss';
import SolarFacilityForm from '../FacilityForm/SolarFacilityForm';
import AddFacilityButton from '../FacilityButtons/AddFacilityButton';
import { AccessibleDatepickerInput } from '../../../common/FormFields';
import type {
  FacilityWithId,
  StepFacilityDataProps,
} from '../FacilityForm/facilityFormTypes';
import { createDeleteFacilityHandler } from './facilityStepUtils';
import FacilityFormTransition from './FacilityFormTransition';

function StepSolarFacilityData({ values, form }: StepFacilityDataProps) {
  const addFacilityButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <section className={s.stepSolarFacilityData}>
      <Heading level={2} className={`${s.heading} ${s.desktopHeading}`}>
        <FormattedMessage id="offerWizard.StepSolarFacilityData.title.desktop" />
      </Heading>
      <Heading level={2} className={`${s.heading} ${s.mobileHeading}`}>
        <FormattedMessage id="offerWizard.StepSolarFacilityData.title.mobile" />
      </Heading>
      <div
        className={`${formStyles.formFields} ${s.preventScrollOnNewFacility}`}
      >
        <div className={formStyles.inputPair}>
          <Field
            name="information.start"
            labelMsg="offerWizard.StepSomeInformation.start.label.directmarketing"
            component={AccessibleDatepickerInput}
            minDate={new Date()}
          />
          <Field
            name="information.end"
            labelMsg="offerWizard.StepSomeInformation.end.label.directmarketing"
            component={AccessibleDatepickerInput}
            minDate={() => {
              if (values.information?.start) {
                return new Date(values.information.start);
              }
              return new Date();
            }}
          />
        </div>
        <hr className={formStyles.separator} />
        <FieldArray
          name="solarFacilityData"
          render={(arrayHelpers) => {
            let facilityForms: JSX.Element[] | undefined;
            const deleteFacility = createDeleteFacilityHandler(
              'solarFacilityData',
              values,
              arrayHelpers,
              addFacilityButtonRef
            );

            if (values.solarFacilityData?.length) {
              facilityForms = values.solarFacilityData.map(
                (data: FacilityWithId, index) => (
                  <FacilityFormTransition key={data.id}>
                    <SolarFacilityForm
                      id={data.id}
                      index={index}
                      values={values}
                      form={form}
                      deleteFacility={deleteFacility}
                    />
                  </FacilityFormTransition>
                )
              );
            }
            return (
              <>
                <TransitionGroup>{facilityForms}</TransitionGroup>
                <AddFacilityButton
                  ref={addFacilityButtonRef}
                  onClick={() => arrayHelpers.push({ id: uuidv4() })}
                />
              </>
            );
          }}
        />
      </div>
    </section>
  );
}

export default StepSolarFacilityData;
